import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { captureException } from "../../exceptions";
import usePrefetchQuery from "../../query-engine/usePrefetchQuery";
/**
 * Generate a hook for us
 * @param getFunction Takes someTypeOfId: string & auth: RequestConfig
 * @param keyMaker Takes someTypeOfId: string and generates a cache key
 * @returns a usable hook
 */
export const makeHook = (getFunction, keyMaker, hookBasedRQOptions = {}) => (id, auth, preFetch = false, perRequestRQOptions = {}) => {
    const options = {
        queryKey: id === null ? undefined : keyMaker(id),
        queryFn: async () => id === null || auth === null ? undefined : getFunction(id, auth),
    };
    const query = useQuery({
        ...hookBasedRQOptions,
        ...perRequestRQOptions,
        ...options,
        enabled: id !== null && auth !== null && preFetch === false,
        onError: (err) => {
            const extras = {
                url: id !== null ? keyMaker(id) : "<null>",
                arg: id ?? "<null>",
                auth: auth ?? "<null>",
            };
            const service = "makeHook";
            if (err instanceof Error) {
                captureException(err, service, extras);
            }
            else {
                const errMsg = `${err}`;
                captureException(new Error(errMsg), service, extras);
            }
        },
    });
    const preFetchStatus = usePrefetchQuery({ ...options }, "prefetch", preFetch && id !== null && auth !== null);
    const result = useMemo(() => {
        if (preFetch) {
            return {
                data: undefined,
                isLoading: true,
                hasError: false,
                preFetch: preFetchStatus,
            };
        }
        if (query.status === "loading" && query.data === undefined) {
            return {
                data: undefined,
                isLoading: true,
                hasError: false,
                preFetch: preFetchStatus,
            };
        }
        if (query.status === "error" && query.data === undefined) {
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                preFetch: preFetchStatus,
                error: query.error,
            };
        }
        if (query.data === undefined) {
            return {
                data: undefined,
                isLoading: true,
                hasError: false,
                preFetch: preFetchStatus,
            };
        }
        return {
            data: query.data,
            isLoading: false,
            hasError: false,
            preFetch: "success",
        };
    }, [preFetch, preFetchStatus, query.data, query.error, query.status]);
    return result;
};
//# sourceMappingURL=makeSingleArgHook.js.map